import type {
  DomainOrchestrator,
} from "./domainOrchestrator";

import {
  recordCharterAccepted,
  recordStageFinished,
  recordStageStarted,
  type MissionLedger,
} from "./missionLedger";

import type {
  ExecutionCharter,
  ExecutiveMissionStatus,
  MissionDomain,
  MissionStageRequest,
  MissionStageResult,
} from "./contracts";

export interface MissionControlDependencies {
  readonly ledger: MissionLedger;

  readonly orchestrators:
    ReadonlyMap<MissionDomain, DomainOrchestrator>;
}

interface MissionRuntimeState {
  charter: ExecutionCharter;

  phase: string;

  completedWorkstreams: string[];
  activeWorkstreams: string[];
  blockedTechnicalWork: string[];

  automaticRecoveryInProgress: boolean;

  progressPercent: number;

  ownerActionRequired: boolean;
}

export class MissionControl {
  private readonly missions =
    new Map<string, MissionRuntimeState>();

  constructor(
    private readonly dependencies: MissionControlDependencies,
  ) {}

  async accept(
    charter: ExecutionCharter,
  ): Promise<void> {
    if (charter.status !== "APPROVED") {
      throw new Error(
        `MISSION_CONTROL_REJECTED_UNAPPROVED_CHARTER:${charter.missionId}`,
      );
    }

    if (this.missions.has(charter.missionId)) {
      throw new Error(
        `MISSION_CONTROL_DUPLICATE_MISSION:${charter.missionId}`,
      );
    }

    this.missions.set(charter.missionId, {
      charter,
      phase: "ACCEPTED",

      completedWorkstreams: [],
      activeWorkstreams: [],
      blockedTechnicalWork: [],

      automaticRecoveryInProgress: false,

      progressPercent: 0,

      ownerActionRequired: false,
    });

    await recordCharterAccepted(
      this.dependencies.ledger,
      charter,
    );
  }

  async dispatchStage(
    request: MissionStageRequest,
  ): Promise<MissionStageResult> {
    const mission = this.missions.get(request.missionId);

    if (!mission) {
      throw new Error(
        `MISSION_CONTROL_UNKNOWN_MISSION:${request.missionId}`,
      );
    }

    const orchestrator =
      this.dependencies.orchestrators.get(request.domain);

    if (!orchestrator) {
      throw new Error(
        `MISSION_CONTROL_NO_ORCHESTRATOR:${request.domain}`,
      );
    }

    mission.phase = request.stageType;

    if (!mission.activeWorkstreams.includes(request.stageId)) {
      mission.activeWorkstreams.push(request.stageId);
    }

    await recordStageStarted(
      this.dependencies.ledger,
      request,
    );

    let result: MissionStageResult;

    try {
      result = await Promise.race([
        orchestrator.execute(request),

        new Promise<never>((_, reject) => {
          const timer = setTimeout(() => {
            reject(
              new Error(
                `MISSION_STAGE_TIMEOUT:${request.stageId}:${request.timeoutMs}`,
              ),
            );
          }, request.timeoutMs);

          timer.unref?.();
        }),
      ]);
    } catch (error) {
      result = {
        missionId: request.missionId,
        stageId: request.stageId,
        status: "RETRYABLE",
        evidence: [
          {
            type: "MISSION_STAGE_EXCEPTION",
            error:
              error instanceof Error
                ? error.message
                : String(error),
          },
        ],
        failureFingerprint:
          error instanceof Error
            ? error.message
            : String(error),
      };
    }

    mission.activeWorkstreams =
      mission.activeWorkstreams.filter(
        value => value !== request.stageId,
      );

    if (result.status === "SUCCEEDED") {
      if (!mission.completedWorkstreams.includes(request.stageId)) {
        mission.completedWorkstreams.push(request.stageId);
      }
    }

    if (
      result.status === "FAILED" ||
      result.status === "RETRYABLE"
    ) {
      if (!mission.blockedTechnicalWork.includes(request.stageId)) {
        mission.blockedTechnicalWork.push(request.stageId);
      }

      mission.automaticRecoveryInProgress = true;
    }

    if (result.status === "BLOCKED_OWNER_DECISION") {
      mission.ownerActionRequired = true;
      mission.automaticRecoveryInProgress = false;
    }

    await recordStageFinished(
      this.dependencies.ledger,
      result,
    );

    return result;
  }

  async getExecutiveStatus(
    missionId: string,
  ): Promise<ExecutiveMissionStatus> {
    const mission = this.missions.get(missionId);

    if (!mission) {
      throw new Error(
        `MISSION_CONTROL_UNKNOWN_MISSION:${missionId}`,
      );
    }

    return {
      missionId,
      phase: mission.phase,

      completedWorkstreams:
        [...mission.completedWorkstreams],

      activeWorkstreams:
        [...mission.activeWorkstreams],

      blockedTechnicalWork:
        [...mission.blockedTechnicalWork],

      automaticRecoveryInProgress:
        mission.automaticRecoveryInProgress,

      progressPercent:
        mission.progressPercent,

      ownerActionRequired:
        mission.ownerActionRequired,
    };
  }
}
